/**
 * Soft≠Live Page36 — Fleet Device Cameras desk (Live full control).
 * tip599: discovery → create → bind → health-probe → test-capture → enqueue.
 * Soft RTSP players / Soft bind success stay deleted.
 */
import { useEffect, useState } from "react";
import { html, F, Card, Field, Input, Alert, Sec, KV } from "./ui.js";
import { path, useAction, useApi, usePolled } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { Receipt } from "./layered-console.js";

export function DeviceCamerasLive({ deviceId, toast }) {
  const act = useAction();
  const camPosture = useApi(path("get__ops_cameras_status"));
  const bindOk = camPosture.loading ? false : (
    !!(camPosture.data && camPosture.data.bind_enqueue_allowed === true)
  );
  const healthOk = camPosture.loading ? false : (
    !!(camPosture.data && camPosture.data.health_probe_allowed === true)
  );
  const probeOk = camPosture.loading ? false : (
    !!(camPosture.data && camPosture.data.probe_test_allowed === true)
  );
  const discoverOk = camPosture.loading ? false : (
    !!(camPosture.data && camPosture.data.catalogue_plane_allowed === true)
  );
  const cameras = useApi(path("get__admin_cameras"));
  const bindings = useApi(
    deviceId ? path("get__admin_devices_p_camera-bindings", { pathParams: [deviceId] }) : null,
  );
  const snaps = useApi(
    deviceId
      ? path("get__admin_camera-snapshots", { query: { device_id: deviceId, limit: 12 } })
      : null,
  );
  const [form, setForm] = useState({
    label: "", ip: "", port: "80", username: "", password: "",
  });
  const [discoverStatus, setDiscoverStatus] = useState("");
  const [discoverRows, setDiscoverRows] = useState([]);
  const [since, setSince] = useState("");
  const [polling, setPolling] = useState(false);
  /** Wave 1 — Live write receipt (never Soft-invented). */
  const [receipt, setReceipt] = useState(null);
  const setF = (k, v) => setForm((x) => ({ ...x, [k]: v }));

  const formHost = () => {
    const ip = String(form.ip || "").trim();
    const port = String(form.port || "80").trim() || "80";
    if (!ip) return "";
    return port === "80" ? ip : `${ip}:${port}`;
  };

  const camItems = (cameras.data && (cameras.data.items || cameras.data.cameras)) || [];
  const bindItems = (bindings.data && (bindings.data.items || bindings.data.bindings)) || [];
  const snapItems = (snaps.data && (snaps.data.items || snaps.data.snapshots)) || [];
  const camsOff = !!(cameras.error && /camera|503|404|disabled|feature/i.test(cameras.error || ""));

  const discovery = usePolled(
    polling && deviceId
      ? path("get__admin_devices_p_camera-discovery", {
          pathParams: [deviceId],
          query: since ? { since } : {},
        })
      : null,
    polling ? 2000 : 0,
  );

  useEffect(() => {
    if (!polling || !discovery.data) return;
    const pack = discovery.data;
    const st = String(pack.status || "pending").toLowerCase();
    if (st === "scanning" || st === "pending") {
      setDiscoverStatus("Scanning LAN…");
      return;
    }
    if (st === "error" || st === "failed") {
      setDiscoverStatus(pack.error || "Discovery failed");
      setDiscoverRows([]);
      setPolling(false);
      return;
    }
    if (st === "complete" || st === "done" || st === "ok"
        || (Array.isArray(pack.devices) && pack.devices.length)) {
      const rows = pack.devices || [];
      setDiscoverStatus("Found " + rows.length + " camera(s)");
      setDiscoverRows(rows);
      setPolling(false);
    }
  }, [polling, discovery.data]);

  useEffect(() => {
    if (!polling) return undefined;
    const t = setTimeout(() => {
      if (polling) {
        setPolling(false);
        setDiscoverStatus((s) => (s && !/^Found/.test(s) ? "Discovery timed out (55s)" : s));
      }
    }, 55000);
    return () => clearTimeout(t);
  }, [polling]);

  const reloadAll = () => {
    cameras.reload && cameras.reload();
    bindings.reload && bindings.reload();
    snaps.reload && snaps.reload();
    toast("Camera list refreshed");
  };

  const startDiscover = async () => {
    if (!discoverOk) {
      toast("Search is closed for this seat", "warn");
      return;
    }
    setSince(new Date().toISOString());
    setDiscoverRows([]);
    setDiscoverStatus("Starting camera search…");
    setPolling(true);
    const out = await act.run(
      path("post__admin_devices_p_camera-discovery", { pathParams: [deviceId] }),
      { method: "POST", body: {} },
    );
    if (!out) {
      setPolling(false);
      setDiscoverStatus(act.error || "Discovery start refused");
      toast(act.error || "Discovery failed", "bad");
    }
  };

  const pickDiscovered = (row) => {
    const host = String(row.host || row.ip || "").trim();
    let ip = host;
    let port = "80";
    if (host.includes("://")) {
      try {
        const u = new URL(host.includes("://") ? host : ("http://" + host));
        ip = u.hostname || host;
        port = String(u.port || 80);
      } catch {
        ip = host;
      }
    } else if (host.includes(":") && host.split(":").length === 2) {
      const parts = host.split(":");
      ip = parts[0];
      port = parts[1] || "80";
    }
    setForm((x) => ({
      ...x,
      ip,
      port,
      label: x.label || String(row.brand || row.brand_hint || row.label || ip || ""),
    }));
    toast("Filled · " + ip + (row.brand || row.brand_hint ? (" · " + (row.brand || row.brand_hint)) : ""), "go");
  };

  const testCapture = async () => {
    if (!probeOk) {
      toast("Test is closed for this seat", "warn");
      return;
    }
    const host = formHost();
    if (!host) { toast("IP required", "bad"); return; }
    const out = await act.run(path("post__admin_cameras_test-capture"), {
      method: "POST",
      body: {
        device_id: deviceId,
        host,
        protocol: "auto",
        username: form.username || "",
        password: form.password || "",
        send_email: false,
      },
    });
    if (!out || out.ok === false) {
      toast((out && (out.tag || out.error)) || act.error || "Test capture refused", "bad");
      return;
    }
    snaps.reload && snaps.reload();
    const sid = String(out.snapshot_id || "").trim();
    setReceipt({
      title: "Test frame",
      accepted: sid || "accepted",
      effective: host,
      evidence: sid ? ("snapshot · " + sid) : "Live test-capture (no Soft still)",
      at: new Date().toISOString(),
      next: "Save & bind when the frame looks right.",
      tone: "go",
    });
    toast(sid
      ? ("Test frame queued" + (out.poll ? " · still fetching" : ""))
      : "Test frame captured", "go");
  };

  const saveAndBind = async () => {
    const host = formHost();
    if (!host) { toast("IP required", "bad"); return; }
    if (!bindOk) {
      toast("Linking cameras stays with the site manager", "warn");
      return;
    }
    const created = await act.run(path("post__admin_cameras"), {
      method: "POST",
      body: {
        label: String(form.label || "").trim() || String(form.ip || "").trim() || host,
        ip: String(form.ip || "").trim(),
        port: Number(form.port || 80) || 80,
        host,
        protocol: "auto",
        brand: "generic",
        username: form.username || "",
        password: form.password || "",
      },
    });
    if (!created) { toast(act.error || "Create camera refused", "bad"); return; }
    const camId = created.camera_id || created.id;
    if (!camId) { toast("Create returned no camera id", "bad"); return; }
    const bound = await act.run(
      path("post__admin_devices_p_camera-bindings", { pathParams: [deviceId] }),
      { method: "POST", body: { camera_id: camId, cooldown_s: 20, burst_count: 1 } },
    );
    if (!bound) { toast(act.error || "Bind refused (camera created)", "warn"); cameras.reload(); return; }
    setForm((x) => ({ ...x, password: "" }));
    cameras.reload();
    bindings.reload();
    setReceipt({
      title: "Registered & bound",
      accepted: String(camId),
      effective: "Board " + String(deviceId),
      evidence: String(created.brand || created.brand_hint || "auto") + " · catalogue + bind",
      at: new Date().toISOString(),
      next: "Health probe, then queue a snapshot to verify.",
      tone: "go",
    });
    toast("Camera saved and bound · " + (created.brand || "auto"), "go");
  };

  const bindExisting = async (cameraId) => {
    if (!bindOk) {
      toast("Linking cameras stays with the site manager", "warn");
      return;
    }
    const out = await act.run(
      path("post__admin_devices_p_camera-bindings", { pathParams: [deviceId] }),
      { method: "POST", body: { camera_id: cameraId, cooldown_s: 20, burst_count: 1 } },
    );
    if (!out) { toast(act.error || "Bind refused", "bad"); return; }
    bindings.reload();
    setReceipt({
      title: "Bound to board",
      accepted: String(cameraId),
      effective: "Board " + String(deviceId),
      evidence: "Live binding write",
      at: new Date().toISOString(),
      next: "Health probe, then queue a snapshot.",
      tone: "go",
    });
    toast("Camera bound to this unit", "go");
  };

  const unbind = async (bindingId) => {
    if (!bindOk) {
      toast("Linking cameras stays with the site manager", "warn");
      return;
    }
    if (!window.confirm("Remove this camera bind?")) return;
    const out = await act.run(
      path("delete__admin_devices_p_camera-bindings_p", {
        pathParams: [deviceId, bindingId],
      }),
      { method: "DELETE" },
    );
    if (!out) { toast(act.error || "Unbind refused", "bad"); return; }
    bindings.reload();
    toast("Camera unbound", "warn");
  };

  const healthProbe = async (cameraId) => {
    if (!healthOk) {
      toast("Health check is closed for this seat", "warn");
      return;
    }
    if (!cameraId) { toast("No camera selected", "warn"); return; }
    const out = await act.run(
      path("post__admin_cameras_p_health-probe", { pathParams: [cameraId] }),
      { method: "POST" },
    );
    if (!out) { toast(act.error || "Health check refused", "bad"); return; }
    cameras.reload && cameras.reload();
    const st = out.health_status || out.status || out.ok;
    const okish = out.ok !== false && st !== "down" && st !== "error";
    toast(okish ? "Camera health looks good" : ("Camera health: " + String(st || "check failed")), okish ? "go" : "warn");
  };

  const captureBound = async () => {
    if (!bindOk) {
      toast("Enqueue needs a site-manager seat", "warn");
      return;
    }
    await enqueueDeviceSnapshot(act, deviceId, {
      toast,
      reason: "Manual capture · device Cameras desk",
    });
    snaps.reload && snaps.reload();
  };

  if (camsOff) {
    return html`<${Alert} tone="info" title="Camera feature not armed"
      say="CAMERA_SNAPSHOT / admin cameras routes unavailable. Soft bind theatre is not invented."/>`;
  }

  return html`<div className="device-cameras device-cameras--liquid" data-cmc-residual="page147-device-cameras"
      data-cmc-cam-receipt=${receipt ? "1" : "0"}>
    ${receipt ? html`<${Receipt} title=${receipt.title} accepted=${receipt.accepted}
      effective=${receipt.effective} evidence=${receipt.evidence} at=${receipt.at}
      next=${receipt.next} tone=${receipt.tone || "go"}/>` : null}
    ${act.envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
      : null}
    <${F}>
    <${Alert} tone="info" title="Cameras on this unit"
      say="Bind a saved camera, check health, then queue a still. LAN / P4 search is optional. Empty list means nothing is bound yet — we never invent a feed."/>

    <div className="btn-row cmc-cam-live__acts" style=${{ marginBottom:12 }} data-cmc-page="page99-device-live">
      <button className="btn btn--ghost btn--sm" type="button"
        disabled=${act.busy}
        onClick=${reloadAll}>Refresh cameras</button>
      <button className="btn btn--go btn--sm" type="button"
        data-cmc-cam-enqueue-ok=${bindOk ? "1" : "0"}
        disabled=${act.busy || !bindItems.length || !bindOk}
        onClick=${captureBound}>
        Queue snapshot</button>
    </div>

    <${Card} title="Bound to this device" meta="Binding controls">
      ${bindings.loading && !bindings.data
        ? html`<p className="note">Loading bindings…</p>`
        : bindings.error
          ? html`<${Alert} tone="warn" title="Bindings unavailable" say=${bindings.error}/>`
          : html`<div className="rows">
              ${!bindItems.length ? html`<p className="note">No camera bound yet.</p>` : null}
              ${bindItems.map((b) => {
                const cam = camItems.find((c) => (c.camera_id || c.id) === b.camera_id);
                const bid = b.binding_id || b.id;
                const camId = b.camera_id || (cam && (cam.camera_id || cam.id));
                return html`
                  <div key=${bid} className="row">
                    <span className="main">
                      <b>${(cam && (cam.label || cam.host)) || camId || "camera"}</b>
                      <span>cooldown ${b.cooldown_s != null ? b.cooldown_s + "s" : "—"}
                        · ${(cam && (cam.health_status || cam.status)) || "bound"}</span>
                    </span>
                    <div className="btn-row" style=${{ margin:0, gap:6 }}>
                      <button className="btn btn--ghost btn--xs"
                        data-cmc-cam-health-ok=${healthOk ? "1" : "0"}
                        disabled=${act.busy || !camId || !healthOk}
                        onClick=${()=>healthProbe(camId)}>Health check</button>
                      <button className="btn btn--danger btn--xs"
                        data-cmc-cam-unbind-ok=${bindOk ? "1" : "0"}
                        disabled=${act.busy || !bindOk}
                        onClick=${()=>unbind(bid)}>Unbind</button>
                    </div>
                  </div>`;
              })}
            </div>`}
    <//>

    <${Card} title="Recent snapshots" meta="Last frames" cls="mt">
      ${snaps.error
        ? html`<${Alert} tone="warn" title="Snapshots unavailable" say=${snaps.error}/>`
        : !snapItems.length
          ? html`<p className="note">${snaps.loading ? "Reading…" : "No snapshots for this device yet."}</p>`
          : html`<div className="rows">
              ${snapItems.slice(0, 12).map((s) => {
                const sid = s.snapshot_id || s.id || "—";
                const at = s.created_at || s.at || "—";
                const gone = !!(s.is_expired || s.expired || !s.has_image);
                return html`
                  <div key=${sid} className="row">
                    <span className="main">
                      <b>${sid}</b>
                      <span>${at}${gone ? " · expired/gone" : ""}</span>
                    </span>
                  </div>`;
              })}
            </div>`}
    <//>

    <${Card} title="Search on the device LAN (P4)" meta="Edge discovery" cls="mt">
      <p className="note">ESP32-P4 searches the unit LAN and annotates IP + brand. Tap a hit to fill the unified form.</p>
      <div className="btn-row">
        <button className="btn btn--ghost btn--sm"
          data-cmc-cam-discover-ok=${discoverOk ? "1" : "0"}
          disabled=${act.busy || polling || !discoverOk}
          onClick=${startDiscover}>
          ${polling ? "Scanning…" : "Search cameras"}</button>
        <span className="note" style=${{ margin:0 }}>${discoverStatus}</span>
      </div>
      ${discovery.error && polling
        ? html`<p className="note">${discovery.error}</p>` : null}
      <div className="chips" style=${{ marginTop:10, flexWrap:"wrap" }}>
        ${discoverRows.map((r, i) => html`
          <button key=${i} type="button" className="chip" data-tone="ai"
            title=${r.host || r.ip || ""}
            onClick=${()=>pickDiscovered(r)}>
            ${(r.brand || r.brand_hint || "ONVIF") + " · " + (r.host || r.ip || r.label || "?")}
          </button>`)}
      </div>
    <//>

    <${Card} title="Register & bind" meta="Unified" cls="mt"
        sub="IP · port · user · password — no Hik/Dahua picker.">
      <div className="grid g2">
        <${Field} label="Label"><${Input} icon="camera" value=${form.label}
          onChange=${(e)=>setF("label", e.target.value)}/><//>
        <${Field} label="IP address"><${Input} icon="map" value=${form.ip}
          onChange=${(e)=>setF("ip", e.target.value)} placeholder="192.168.1.72" autocomplete="off"/><//>
        <${Field} label="Port"><${Input} icon="map" value=${form.port}
          onChange=${(e)=>setF("port", e.target.value)} placeholder="80" autocomplete="off"/><//>
        <${Field} label="Username"><${Input} icon="user" value=${form.username}
          onChange=${(e)=>setF("username", e.target.value)} autocomplete="off"/><//>
        <${Field} label="Password"><${Input} icon="lock" type="password" value=${form.password}
          onChange=${(e)=>setF("password", e.target.value)} autocomplete="new-password"/><//>
      </div>
      <div className="btn-row" style=${{ marginTop:12 }}>
        <button className="btn btn--ghost btn--sm"
          data-cmc-cam-probe-ok=${probeOk ? "1" : "0"}
          disabled=${act.busy || !formHost() || !probeOk}
          onClick=${testCapture}>Test frame</button>
        <button className="btn btn--go btn--sm"
          data-cmc-cam-bind-ok=${bindOk ? "1" : "0"}
          disabled=${act.busy || !formHost() || !bindOk}
          onClick=${saveAndBind}>${act.busy ? "Writing…" : "Save & bind"}</button>
      </div>
    <//>

    <${Card} title="Catalogue (bind existing)" meta="Your cameras" cls="mt">
      <${Sec} title="Unbound cameras you already own">
        <div className="rows">
          ${camItems.filter((c) => {
            const id = c.camera_id || c.id;
            return id && !bindItems.some((b) => b.camera_id === id);
          }).slice(0, 24).map((c) => {
            const id = c.camera_id || c.id;
            return html`
              <div key=${id} className="row">
                <span className="main"><b>${c.label || id}</b>
                  <span>${c.host || "—"} · ${c.health_status || c.status || "—"}</span></span>
                <div className="btn-row" style=${{ margin:0, gap:6 }}>
                  <button className="btn btn--ghost btn--xs"
                    data-cmc-cam-health-ok=${healthOk ? "1" : "0"}
                    disabled=${act.busy || !healthOk}
                    onClick=${()=>healthProbe(id)}>Health check</button>
                  <button className="btn btn--ghost btn--xs" disabled=${act.busy || !bindOk}
                    onClick=${()=>bindExisting(id)}>Bind</button>
                </div>
              </div>`;
          })}
          ${!camItems.length ? html`<p className="note">Catalogue empty — create above or under Settings → Cameras.</p>` : null}
        </div>
      <//>
    <//>
    <//>
  </div>`;
}

/** Manual CAM enqueue — uses open alarm id or unix seconds as trigger_event_id. */
export async function enqueueDeviceSnapshot(act, deviceId, { alarmId, toast, reason }) {
  let trigger = 0;
  if (alarmId != null && alarmId !== "") {
    const n = Number(String(alarmId).replace(/^ALM-/i, ""));
    if (Number.isFinite(n) && n >= 1) trigger = Math.trunc(n);
  }
  if (trigger < 1) trigger = Math.max(1, Math.floor(Date.now() / 1000));
  const out = await act.run(
    path("post__admin_devices_p_camera-snapshots_enqueue", { pathParams: [deviceId] }),
    { method: "POST", body: { trigger_event_id: trigger } },
  );
  if (!out) {
    toast(act.error || "Snapshot could not start. Bind a camera first or ask a manager.", "bad");
    return null;
  }
  const n = out.enqueued != null ? out.enqueued : 0;
  const green = out.frame_observed === true || out.enqueue_claims_frame === true;
  const stage = String(out.outcome_stage || "queued");
  toast(
    green
      ? ((reason || "Snapshot queued")
        + (Number(n) > 0 ? (" · " + n + " job" + (Number(n) === 1 ? "" : "s")) : ""))
      : ((reason || "Snapshot queued")
        + (Number(n) > 0 ? (" · " + n + " job" + (Number(n) === 1 ? "" : "s")) : "")
        + " · " + stage + " · not a frame yet"),
    green ? "go" : "warn",
  );
  return out;
}
